import { supabase } from '../supabase';

/** Generic message returned to client on errors (do not expose error.message). */
const USER_FACING_ERROR = 'Something went wrong. Please try again.';

/**
 * Feedback Service
 * Handles submission and retrieval of user feedback, suggestions, bug reports, and biohack requests
 */
class FeedbackService {
  /**
   * Submit feedback
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.type - Type: 'feedback', 'suggestion', 'bug_report', 'biohack_request'
   * @param {string} feedbackData.title - Optional title
   * @param {string} feedbackData.content - Feedback content (required)
   * @param {string} feedbackData.priority - Optional priority: 'low', 'normal', 'high', 'urgent'
   * @returns {Promise<Object>} - API response
   */
  async submitFeedback(feedbackData) {
    try {
      const { type, title, content, priority = 'normal' } = feedbackData;

      // Validate required fields
      if (!type || !content || !content.trim()) {
        throw new Error('Type and content are required');
      }

      // Validate type
      const validTypes = ['feedback', 'suggestion', 'bug_report', 'biohack_request'];
      if (!validTypes.includes(type)) {
        throw new Error(`Invalid type. Must be one of: ${validTypes.join(', ')}`);
      }

      // Get current user (if authenticated)
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || null;

      // Prepare feedback payload
      const payload = {
        user_id: userId,
        type: type,
        content: content.trim(),
        priority: priority,
        status: 'pending'
      };

      // Add title if provided
      if (title && title.trim()) {
        payload.title = title.trim();
      }

      // Insert feedback into database
      const { data, error } = await supabase
        .from('feedback')
        .insert([payload])
        .select()
        .single();

      if (error) {
        console.error('Error submitting feedback:', error);
        throw new Error(error.message || 'Failed to submit feedback');
      }

      return {
        success: true,
        data: data,
        message: 'Thank you for your feedback! We appreciate your input.'
      };
    } catch (error) {
      console.error('Error in submitFeedback:', error);
      return {
        success: false,
        error: USER_FACING_ERROR,
        message: USER_FACING_ERROR
      };
    }
  }

  /**
   * Get user's feedback history (only if authenticated)
   * @param {number} limit - Maximum number of feedback items to retrieve
   * @returns {Promise<Array>} - Array of feedback items
   */
  async getUserFeedback(limit = 10) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return [];
      }

      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching user feedback:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserFeedback:', error);
      return [];
    }
  }

  /**
   * Update user's own feedback (only if status is pending)
   * @param {string} feedbackId - Feedback ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated feedback
   */
  async updateFeedback(feedbackId, updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Must be authenticated to update feedback');
      }

      // Only allow updating title and content, and only if status is pending
      const allowedUpdates = {};
      if (updates.title !== undefined) {
        allowedUpdates.title = updates.title.trim();
      }
      if (updates.content !== undefined) {
        allowedUpdates.content = updates.content.trim();
      }

      const { data, error } = await supabase
        .from('feedback')
        .update(allowedUpdates)
        .eq('id', feedbackId)
        .eq('user_id', user.id)
        .eq('status', 'pending') // Only allow updates if status is pending
        .select()
        .single();

      if (error) {
        console.error('Error updating feedback:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Feedback not found or cannot be updated');
      }

      return {
        success: true,
        data: data,
        message: 'Feedback updated successfully'
      };
    } catch (error) {
      console.error('Error in updateFeedback:', error);
      return {
        success: false,
        error: USER_FACING_ERROR,
        message: USER_FACING_ERROR
      };
    }
  }
}

// Create and export a singleton instance
const feedbackService = new FeedbackService();
export default feedbackService;